import { Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import { CornerUpLeft } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@sara/ui/components/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sara/ui/components/dropdown-menu';
import { cn } from '@sara/ui/lib/utils';

export interface ResolvedCrumb {
  key: string;
  label: string | null;
  to?: string;
  isCurrent: boolean;
  isLoading: boolean;
}

interface HeaderBreadcrumbsProps {
  crumbs: ResolvedCrumb[];
}

const baseLabelClass = 'text-xs font-medium leading-tight whitespace-nowrap';

const pillChromeClass = cn(
  'rounded-full border border-border bg-secondary/40 px-3 py-1',
  'backdrop-blur transition-colors'
);

const parentLinkClass = cn(
  baseLabelClass,
  'text-muted-foreground transition-colors',
  'hover:text-foreground focus-visible:text-foreground',
  'focus-visible:outline-none rounded-sm'
);

export function HeaderBreadcrumbs({ crumbs }: HeaderBreadcrumbsProps) {
  if (crumbs.length === 0) return null;

  return (
    <>
      <DesktopBreadcrumbs crumbs={crumbs} />
      <MobileBreadcrumbs crumbs={crumbs} />
    </>
  );
}

function DesktopBreadcrumbs({ crumbs }: HeaderBreadcrumbsProps) {
  return (
    <Breadcrumb className="hidden min-w-0 shrink md:block">
      <BreadcrumbList
        className={cn('flex-nowrap gap-1.5 overflow-hidden whitespace-nowrap', pillChromeClass)}
      >
        {crumbs.map((crumb, index) => (
          <Fragment key={crumb.key}>
            <BreadcrumbItem className="min-w-0 shrink-0 last:min-w-0 last:shrink">
              <CrumbNode crumb={crumb} />
            </BreadcrumbItem>
            {index < crumbs.length - 1 && <Separator />}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function MobileBreadcrumbs({ crumbs }: HeaderBreadcrumbsProps) {
  const current = crumbs[crumbs.length - 1];
  const ancestors = crumbs.slice(0, -1);
  const linkable = ancestors.filter((c): c is ResolvedCrumb & { to: string } => Boolean(c.to));

  // Single-crumb pages have nowhere to navigate up to — render a static pill.
  if (linkable.length === 0) {
    return (
      <nav
        aria-label="Breadcrumb"
        className={cn(
          pillChromeClass,
          'inline-flex min-w-0 max-w-[55%] shrink-0 items-center md:hidden'
        )}
      >
        <CurrentLabel crumb={current} />
      </nav>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Open navigation breadcrumbs"
        className={cn(
          'md:hidden',
          'inline-flex min-w-0 max-w-[55%] shrink-0 items-center gap-1.5',
          pillChromeClass,
          'hover:border-foreground/30',
          'data-[state=open]:border-foreground/30 data-[state=open]:bg-secondary/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        )}
      >
        <span
          aria-hidden
          className="text-xs font-medium leading-none text-muted-foreground/80 tracking-wider"
        >
          …
        </span>
        <span aria-hidden className="text-[12px] font-light leading-none text-foreground/30">
          /
        </span>
        <CurrentLabel crumb={current} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="min-w-[12rem] rounded-xl border-border/80 bg-popover/95 p-1.5 shadow-lg backdrop-blur"
      >
        {ancestors.length > 0 && (
          <div
            className="flex items-center gap-1.5 px-2 pb-1.5 pt-1 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground/75"
            aria-hidden
          >
            {ancestors.map((c, i) => (
              <Fragment key={`eyebrow-${c.key}`}>
                {i > 0 && <span className="text-foreground/25">/</span>}
                <span className="truncate">{c.label}</span>
              </Fragment>
            ))}
          </div>
        )}
        {ancestors.length > 0 && <div className="mx-1 mb-1 h-px bg-border/60" aria-hidden />}
        {linkable.map((c) => (
          <DropdownMenuItem
            key={c.key}
            asChild
            className={cn(
              'cursor-pointer rounded-lg px-2 py-2 text-[13px] font-medium',
              'text-foreground/85 focus:bg-secondary/80 focus:text-foreground'
            )}
          >
            <Link to={c.to} className="flex w-full items-center gap-2.5">
              <CornerUpLeft
                strokeWidth={1.6}
                className="h-3.5 w-3.5 shrink-0 text-foreground/45"
                aria-hidden
              />
              <span className="truncate">{c.label}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CurrentLabel({ crumb }: { crumb: ResolvedCrumb }) {
  if (crumb.isLoading || crumb.label === null) return <CrumbSkeleton />;
  return (
    <span className={cn(baseLabelClass, 'min-w-0 truncate text-foreground')}>{crumb.label}</span>
  );
}

function CrumbNode({ crumb }: { crumb: ResolvedCrumb }) {
  if (crumb.isLoading || crumb.label === null) {
    return <CrumbSkeleton />;
  }
  if (crumb.isCurrent) {
    return (
      <BreadcrumbPage className={cn(baseLabelClass, 'min-w-0 truncate text-foreground')}>
        {crumb.label}
      </BreadcrumbPage>
    );
  }
  if (!crumb.to) {
    return <span className={parentLinkClass}>{crumb.label}</span>;
  }
  return (
    <BreadcrumbLink asChild>
      <Link to={crumb.to} className={parentLinkClass}>
        {crumb.label}
      </Link>
    </BreadcrumbLink>
  );
}

function Separator() {
  return (
    <BreadcrumbSeparator className="shrink-0 select-none [&>svg]:hidden">
      <span aria-hidden className="text-[12px] font-light text-foreground/30">
        /
      </span>
    </BreadcrumbSeparator>
  );
}

function CrumbSkeleton() {
  return (
    <output className="inline-flex items-center gap-1" aria-label="Loading breadcrumb">
      <Dot delay="0ms" />
      <Dot delay="120ms" />
      <Dot delay="240ms" />
    </output>
  );
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      aria-hidden
      className="h-1 w-1 animate-pulse rounded-full bg-foreground/35"
      style={{ animationDelay: delay, animationDuration: '1.1s' }}
    />
  );
}
